import { noop } from 'lodash'
import { RefObject, useEffect, useState } from 'react'
import { CURSOR_IMAGES, CURSOR_STATES } from '../TableCell.constants'

type CursorState = (typeof CURSOR_STATES)[keyof typeof CURSOR_STATES]

interface UseCursorStateProps {
  ref: RefObject<HTMLElement>
  enabled?: boolean
}

export const useCursorState = ({ ref, enabled }: UseCursorStateProps) => {
  const [cursorState, setCursorState] = useState<CursorState>(
    CURSOR_STATES.DEFAULT
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setCursorState(CURSOR_STATES.DEFAULT)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref])

  const getCursorStyle = (): string => {
    switch (cursorState) {
      case CURSOR_STATES.COPY:
        return `url("${CURSOR_IMAGES.COPY}"), pointer`
      case CURSOR_STATES.COPIED:
        return `url("${CURSOR_IMAGES.COPIED}"), pointer`
      default:
        return CURSOR_STATES.DEFAULT
    }
  }

  const handleMouseEnter = (): void => {
    if (cursorState !== CURSOR_STATES.COPIED) {
      setCursorState(CURSOR_STATES.COPY)
    }
  }

  const handleMouseLeave = (): void => {
    if (cursorState !== CURSOR_STATES.COPIED) {
      setCursorState(CURSOR_STATES.DEFAULT)
    }
  }

  const setCopied = (): void => {
    setCursorState(CURSOR_STATES.COPIED)

    setTimeout(() => {
      setCursorState(CURSOR_STATES.DEFAULT)
    }, 2000)
  }

  if (!enabled) {
    return {
      cursorState: CURSOR_STATES.DEFAULT,
      getCursorStyle: () => CURSOR_STATES.DEFAULT,
      handleMouseEnter: noop,
      handleMouseLeave: noop,
      setCopied: noop
    }
  }

  return {
    cursorState,
    getCursorStyle,
    handleMouseEnter,
    handleMouseLeave,
    setCopied
  }
}
